import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { v, num, PLACEHOLDER, FieldRow, Section, SectionNumberingProvider, TemplateDocument, TemplateFooter, TemplateLetterhead, } from '../../../atoms/index.js';
export const metadata = {
    // Document sections in fixed order (per ADR-0049). § numbers are
    // computed from visibility; refs are the stable identity. Field-less
    // sections (static text / signing / bilagor) are declared so they
    // get numbered even though they carry no editor field.
    sections: [
        { ref: 'hyresvard', title: 'Hyresvärd' },
        { ref: 'hyresgast', title: 'Hyresgäst' },
        { ref: 'avtalskoppling', title: 'Avtalskoppling' },
        { ref: 'indexreglering', title: 'Indexreglering' },
    ],
    templateKey: 'indexavtal',
    version: 'v1',
    category: 'Lokalhyra',
    title: 'Indexavtal · Version 2026',
    shortTitle: 'Indexavtal',
    formNumber: 'Formulär R-08',
    preamble: 'Mellan hyresvärd och hyresgäst har följande avtal om indexering ingåtts.',
    description: 'Indexreglering av hyran i ett lokalhyresavtal. Tillägg till hyran ' +
        'beräknas på bashyran utifrån förändringar i konsumentprisindex ' +
        '(KPI, basår 2020).\n\n' +
        'Skapas endast som bilaga under ett lokalhyresavtal — visas inte i ' +
        'avtalslistan, har ingen egen signering. Bifogas det signerade ' +
        'avtalet automatiskt vid utskick via Scrive.',
    status: 'active',
    attachableTo: ['lokalhyra'],
    singletonPerParent: true,
    partySlots: [],
};
export const variableSchema = [
    // — Avtalskoppling —
    {
        key: 'attachment_type',
        label: 'Typ av koppling',
        type: 'select',
        section: 'avtalskoppling',
        requiredAtFinal: true,
        default: 'bilaga',
        options: [
            { value: 'bilaga', label: 'Bilaga till avtal (kompletterar avtalet)', code: 'appendix', summary: 'Attached as an appendix that supplements the contract.' },
            { value: 'tillägg', label: 'Tillägg till avtal (ändrar villkor)', code: 'amendment', summary: 'Attached as an amendment that changes the contract terms.' },
        ],
        help: '"Tillägg" ändrar villkoren i lokalhyresavtalet (vanligast för ' +
            'indexering). "Bilaga" kompletterar avtalet utan att ändra det.',
    },
    {
        key: 'parent_contract_number',
        label: 'Avtalsnummer',
        type: 'text',
        section: 'avtalskoppling',
        requiredAtFinal: true,
        derivedFromParent: true,
        derivedFrom: 'contract_number',
        help: 'Hämtas från lokalhyresavtalets avtalsnummer.',
    },
    // — Hyresvärd —
    {
        key: 'landlord_name',
        label: 'Namn',
        type: 'text',
        section: 'hyresvard',
        requiredAtFinal: true,
        derivedFromParent: true,
        help: 'Hämtas från lokalhyresavtalet.',
    },
    {
        key: 'landlord_org_number',
        label: 'Organisationsnummer',
        type: 'text',
        section: 'hyresvard',
        requiredAtFinal: true,
        derivedFromParent: true,
        help: 'Hämtas från lokalhyresavtalet.',
    },
    {
        key: 'landlord_address',
        label: 'Gatuadress',
        type: 'text',
        section: 'hyresvard',
        requiredAtFinal: true,
        derivedFromParent: true,
        help: 'Hämtas från lokalhyresavtalet.',
    },
    {
        key: 'landlord_postal_address',
        label: 'Postnummeradress',
        type: 'text',
        section: 'hyresvard',
        requiredAtFinal: true,
        derivedFromParent: true,
        help: 'Hämtas från lokalhyresavtalet.',
    },
    // — Hyresgäst —
    {
        key: 'tenant_name',
        label: 'Namn',
        type: 'text',
        section: 'hyresgast',
        requiredAtFinal: true,
        derivedFromParent: true,
        help: 'Hämtas från lokalhyresavtalet.',
    },
    {
        key: 'tenant_org_number',
        label: 'Organisationsnummer',
        type: 'text',
        section: 'hyresgast',
        requiredAtFinal: true,
        derivedFromParent: true,
        help: 'Hämtas från lokalhyresavtalet.',
    },
    {
        key: 'tenant_address',
        label: 'Gatuadress',
        type: 'text',
        section: 'hyresgast',
        requiredAtFinal: true,
        derivedFromParent: true,
        help: 'Hämtas från lokalhyresavtalet.',
    },
    {
        key: 'tenant_postal_address',
        label: 'Postnummeradress',
        type: 'text',
        section: 'hyresgast',
        requiredAtFinal: true,
        derivedFromParent: true,
        help: 'Hämtas från lokalhyresavtalet.',
    },
    // — Indexering —
    {
        key: 'index_clause_mode',
        label: 'Indexregleringsklausul',
        type: 'select',
        section: 'indexreglering',
        requiredAtFinal: true,
        default: 'standard',
        options: [
            { value: 'standard', label: 'Standardklausul (KPI, basår 2020)', code: 'standard_kpi', summary: 'Use the standard CPI index clause (base year 2020).' },
            { value: 'manual', label: 'Helt manuell input (fritext)', code: 'manual_freetext', summary: 'Write the entire index-adjustment clause by hand as free text.' },
        ],
        help: 'Standardklausulen använder den legala KPI-formuleringen. Välj "Helt ' +
            'manuell input" för att skriva hela indexregleringsklausulen för hand.',
    },
    {
        key: 'index_manual_text',
        label: 'Indexregleringsklausul (fritext)',
        type: 'textarea',
        section: 'indexreglering',
        requiredAtFinal: true,
        visibleWhen: { index_clause_mode: 'manual' },
        help: 'Skriv hela indexregleringsklausulen. Ersätter standardklausulen i avtalet.',
    },
    {
        key: 'base_rent_mode',
        label: 'Bashyran anges som',
        type: 'select',
        section: 'indexreglering',
        requiredAtFinal: true,
        default: 'amount',
        visibleWhen: { index_clause_mode: 'standard' },
        options: [
            { value: 'amount', label: 'Belopp (kr)', code: 'amount', summary: 'Base rent is given as a SEK amount.' },
            { value: 'percent', label: 'Procent av angiven hyra', code: 'percent', summary: 'Base rent is given as a percentage of the stated rent.' },
        ],
    },
    {
        key: 'base_rent_amount',
        label: 'Bashyra (kr)',
        type: 'currency',
        min: 0,
        section: 'indexreglering',
        requiredAtFinal: true,
        visibleWhen: { index_clause_mode: 'standard', base_rent_mode: 'amount' },
        derivedFromParent: true,
        derivedFrom: 'rent_amount',
        help: 'Hämtas från lokalhyresavtalets hyra. Lås upp för att ange en avvikande bashyra.',
    },
    {
        key: 'base_rent_percent',
        label: 'Bashyra (% av hyran)',
        type: 'number',
        min: 0,
        max: 100,
        section: 'indexreglering',
        requiredAtFinal: true,
        visibleWhen: { index_clause_mode: 'standard', base_rent_mode: 'percent' },
        help: 'Anges utan procenttecken. T.ex. "100" = hela hyran.',
    },
    {
        key: 'base_year_override',
        label: 'Avvikande bastal — år',
        type: 'text',
        section: 'indexreglering',
        visibleWhen: { index_clause_mode: 'standard' },
        help: 'Lämnas tomt om standardregeln gäller (avtal som börjar 1/1–30/6 → ' +
            'oktober året innan; 1/7–31/12 → oktober samma år). Fyll i endast ' +
            'om annat bastal ska gälla. Ange enbart årtal (t.ex. "2024").',
    },
];
export const sampleValues = {
    attachment_type: 'bilaga',
    parent_contract_number: 'A-2042',
    landlord_name: 'Fastighetsbolaget Söder AB',
    landlord_org_number: '556789-1234',
    landlord_address: 'Drottninggatan 100',
    landlord_postal_address: '111 60 Stockholm',
    tenant_name: 'Café Söder HB',
    tenant_org_number: '969712-5678',
    tenant_address: 'Götgatan 35',
    tenant_postal_address: '116 21 Stockholm',
    index_clause_mode: 'standard',
    index_manual_text: '',
    base_rent_mode: 'amount',
    base_rent_amount: 480000,
    base_rent_percent: 100,
    base_year_override: '',
};
const cond = (mode, predicate) => mode === 'blank' ? true : predicate;
export function Letterhead() {
    return (_jsx(TemplateLetterhead, { title: metadata.title, formNumber: metadata.formNumber }));
}
export function PageFooter() {
    return _jsx(TemplateFooter, { formNumber: metadata.formNumber });
}
export function Body({ values, mode, }) {
    const isManual = values.index_clause_mode === 'manual';
    const showStandard = cond(mode, !isManual);
    const showManual = cond(mode, isManual);
    const showAmount = cond(mode, values.base_rent_mode !== 'percent');
    const showPercent = cond(mode, values.base_rent_mode === 'percent');
    const showOverride = cond(mode, !!values.base_year_override);
    return (_jsxs(SectionNumberingProvider, { sections: metadata.sections ?? [], values: values, mode: mode, children: [_jsx("p", { className: "mb-4 italic text-stone-600", children: metadata.preamble }), _jsxs("div", { className: "grid grid-cols-2 gap-6", children: [_jsxs(Section, { sectionRef: "hyresvard", title: "Hyresv\u00E4rd", children: [_jsx(FieldRow, { label: "Namn", children: v(values.landlord_name) }), _jsx(FieldRow, { label: "Org.nr", children: v(values.landlord_org_number) }), _jsx(FieldRow, { label: "Gatuadress", children: v(values.landlord_address) }), _jsx(FieldRow, { label: "Postadress", children: v(values.landlord_postal_address) })] }), _jsxs(Section, { sectionRef: "hyresgast", title: "Hyresg\u00E4st", children: [_jsx(FieldRow, { label: "Namn", children: v(values.tenant_name) }), _jsx(FieldRow, { label: "Org.nr", children: v(values.tenant_org_number) }), _jsx(FieldRow, { label: "Gatuadress", children: v(values.tenant_address) }), _jsx(FieldRow, { label: "Postadress", children: v(values.tenant_postal_address) })] })] }), _jsx(Section, { sectionRef: "avtalskoppling", title: "Avtalskoppling", children: _jsxs("div", { className: "flex flex-col gap-1 text-[10px] text-stone-700", children: [_jsxs("div", { children: [_jsx("span", { className: "mr-1", children: cond(mode, values.attachment_type === 'tillägg') ? '☑' : '☐' }), "Till\u00E4gg till avtal nr:", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.parent_contract_number) })] }), _jsxs("div", { children: [_jsx("span", { className: "mr-1", children: cond(mode, values.attachment_type === 'bilaga') ? '☑' : '☐' }), "Bilaga till avtal nr:", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.parent_contract_number) })] })] }) }), _jsxs(Section, { sectionRef: "indexreglering", title: "Indexreglering", children: [showManual && (_jsx("p", { className: "whitespace-pre-wrap text-[10px] leading-relaxed text-stone-700", children: v(values.index_manual_text) })), showStandard && (_jsxs(_Fragment, { children: [_jsxs("p", { className: "mb-3 text-[10px] leading-relaxed text-stone-700", children: ["Av det i kontraktet angivna hyresbeloppet ska", ' ', showAmount && (_jsx("span", { className: "border-b border-stone-300 font-serif", children: typeof values.base_rent_amount === 'number'
                                            ? `${num(values.base_rent_amount)} kr`
                                            : `${PLACEHOLDER} kr` })), showAmount && showPercent && ' eller ', showPercent && (_jsx("span", { className: "border-b border-stone-300 font-serif", children: typeof values.base_rent_percent === 'number'
                                            ? `${num(values.base_rent_percent)} %`
                                            : `${PLACEHOLDER} %` })), ' ', "utg\u00F6ra bashyra. Under hyrestiden ska med h\u00E4nsyn till f\u00F6r\u00E4ndringarna i konsumentprisindex (totalindex med 2020 som bas\u00E5r) till\u00E4gg till hyresbeloppet utg\u00E5 med en viss procent av bashyran enligt nedanst\u00E5ende grunder."] }), _jsxs("p", { className: "mb-3 text-[10px] leading-relaxed text-stone-700", children: ["F\u00F6r hyresavtal som b\u00F6rjar l\u00F6pa n\u00E5gon g\u00E5ng under tiden 1/1\u201330/6 anses bashyran anpassad till indextalet f\u00F6r oktober m\u00E5nad \u00E5ret innan. F\u00F6r hyresavtal som b\u00F6rjar l\u00F6pa n\u00E5gon g\u00E5ng under tiden 1/7\u201331/12 anses bashyran i st\u00E4llet anpassad till indextalet f\u00F6r oktober m\u00E5nad under samma tid. Indextalet f\u00F6r den oktober m\u00E5nad till vilken bashyran enligt ovan anses anpassad utg\u00F6r bastal s\u00E5vida inte annat avtalats", showOverride && (_jsxs(_Fragment, { children: [": indextalet f\u00F6r oktober m\u00E5nad \u00E5r", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.base_year_override) })] })), "."] }), _jsx("p", { className: "mb-3 text-[10px] leading-relaxed text-stone-700", children: "Skulle indextalet n\u00E5gon p\u00E5f\u00F6ljande oktober m\u00E5nad ha stigit i f\u00F6rh\u00E5llande till bastalet, ska till\u00E4gg utg\u00E5 med det procenttal varmed indextalet \u00E4ndrats i f\u00F6rh\u00E5llande till bastalet. I forts\u00E4ttningen ska till\u00E4gg utg\u00E5 i f\u00F6rh\u00E5llande till index\u00E4ndringarna, varvid hyresf\u00F6r\u00E4ndringen ber\u00E4knas p\u00E5 basis av den procentuella f\u00F6r\u00E4ndringen mellan bastalet och indextalet f\u00F6r respektive oktober m\u00E5nad. Till\u00E4gget kan bli s\u00E5v\u00E4l h\u00F6gre som l\u00E4gre mellan olika \u00E5r. Utg\u00E5ende hyra ska dock aldrig s\u00E4ttas l\u00E4gre \u00E4n det i kontraktet angivna hyresbeloppet. Hyres\u00E4ndringen sker alltid fr.o.m. den 1 januari efter det att oktoberindex f\u00F6ranlett omr\u00E4kning." }), cond(mode, !values.base_year_override) && (_jsx("p", { className: "text-[10px] leading-relaxed text-stone-700", children: "Om bastalet utg\u00F6rs av indextal f\u00F6r m\u00E5nad med fastst\u00E4llt indextal med bas\u00E5r 1980 (oktober m\u00E5nad t.o.m. \u00E5r 2025) ska indextalet omr\u00E4knas till index med bas\u00E5r 2020 genom att bastalet divideras med medeltalet av KPI f\u00F6r 2020 (335,92 med bas\u00E5r 1980) och kvoten multipliceras med 100, avrundat till tv\u00E5 decimaler." }))] }))] }), _jsx("p", { className: "mt-6 text-[10px] italic leading-relaxed text-stone-600", children: "Detta indexavtal utg\u00F6r en del av lokalhyresavtalet och undertecknas elektroniskt tillsammans med det. Egen underskrift kr\u00E4vs inte p\u00E5 denna handling." })] }));
}
export function Component(props) {
    return (_jsx(TemplateDocument, { header: _jsx(Letterhead, {}), footer: _jsx(PageFooter, {}), children: _jsx(Body, { ...props }) }));
}
//# sourceMappingURL=index.js.map